import type { DatenschutzFassung, DatenschutzText } from '../../../services/sifa/types';
import { DATENSCHUTZ_MUSTER_TEXT } from '../../../services/sifa/datenschutzMuster';
import { formatZeit } from '../unterweisungModulLogic';

/**
 * Regeln des Reiters „Datenschutzhinweise" (docs/sifa-ausbau2-plan.md §0.4)
 * — rein, ohne React. Die Vorschau setzt den Text als Bloecke zusammen
 * (Ueberschrift, Absatz, Liste) statt HTML einzuspeisen: der Text ist
 * Klartext mit '## ' und '- ', und offene `[…]`-Stellen sollen markiert
 * sichtbar sein, wie spaeter auf der Teilnehmerseite.
 */

/** Beispielnamen der Vorschau — beim Kunden setzt die Teilnehmerseite die echten ein. */
export const BEISPIEL_ARBEITGEBER = 'Musterfirma GmbH';
/** Nur, wenn der eigene Firmenname (Profil) fehlt. */
export const BEISPIEL_AUFTRAGSVERARBEITER = 'Ihr Büro';

/** Auftragsverarbeiter ist das eigene Buero — wie spaeter auf der Teilnehmerseite (bueroName). */
export const beispielNamen = (bueroName?: string | null): { arbeitgeber: string; auftragsverarbeiter: string } => ({
  arbeitgeber: BEISPIEL_ARBEITGEBER,
  auftragsverarbeiter: (bueroName ?? '').trim() || BEISPIEL_AUFTRAGSVERARBEITER,
});

export const FASSUNGEN_HINWEIS =
  'Jede gespeicherte Fassung bleibt erhalten; Teilnahmen merken sich, welche Fassung bestätigt wurde.';

export const MUSTER_GILT_HINWEIS = 'Noch keine eigene Fassung — es gilt das Muster.';

export const FRAGE_MUSTER_ERSETZT =
  'Muster übernehmen? Der Text im Feld wird durch das Muster ersetzt. Gespeicherte Fassungen bleiben unberührt; erst „Speichern" legt eine neue an.';

export type DatenschutzBlock =
  | { art: 'ueberschrift'; text: string }
  | { art: 'absatz'; zeilen: string[] }
  | { art: 'liste'; punkte: string[] };

/**
 * Zerlegt den Klartext: Leerzeile trennt Bloecke, '## ' ist eine
 * Ueberschrift, '- ' ein Listenpunkt; aufeinanderfolgende Zeilen eines
 * Absatzes bleiben Zeilen (Anschrift, Telefon …).
 */
export const datenschutzBloecke = (text: string): DatenschutzBlock[] => {
  const bloecke: DatenschutzBlock[] = [];
  let absatz: string[] | null = null;
  let liste: string[] | null = null;
  const schliessen = () => {
    if (absatz) bloecke.push({ art: 'absatz', zeilen: absatz });
    if (liste) bloecke.push({ art: 'liste', punkte: liste });
    absatz = null;
    liste = null;
  };
  for (const roh of (text ?? '').replace(/\r\n?/g, '\n').split('\n')) {
    const zeile = roh.trim();
    if (!zeile) { schliessen(); continue; }
    if (zeile.startsWith('## ')) {
      schliessen();
      bloecke.push({ art: 'ueberschrift', text: zeile.slice(3).trim() });
      continue;
    }
    if (zeile.startsWith('- ')) {
      if (absatz) schliessen();
      (liste ??= []).push(zeile.slice(2).trim());
      continue;
    }
    if (liste) schliessen();
    (absatz ??= []).push(zeile);
  }
  schliessen();
  return bloecke;
};

/** Eine Zeile in Text und offene `[…]`-Stellen — die markiert die Vorschau. */
export const teileMitLuecken = (zeile: string): { text: string; luecke: boolean }[] => {
  const teile: { text: string; luecke: boolean }[] = [];
  const muster = /\[[^[\]\n]+\]/g;
  let ab = 0;
  for (const treffer of (zeile ?? '').matchAll(muster)) {
    const i = treffer.index ?? 0;
    if (i > ab) teile.push({ text: zeile.slice(ab, i), luecke: false });
    teile.push({ text: treffer[0], luecke: true });
    ab = i + treffer[0].length;
  }
  if (ab < (zeile ?? '').length) teile.push({ text: zeile.slice(ab), luecke: false });
  return teile;
};

/** Rueckfrage nur, wenn im Feld schon eigener Text steht (leer oder schon das Muster: sofort). */
export const musterBrauchtRueckfrage = (text: string): boolean => {
  const t = (text ?? '').trim();
  return t !== '' && t !== DATENSCHUTZ_MUSTER_TEXT.trim();
};

/** Vergleichsform: Zeilenenden vereinheitlicht, Rand ohne Leerraum. */
const vergleichsText = (t: string | null | undefined): string => (t ?? '').replace(/\r\n?/g, '\n').trim();

/**
 * Speichern nur mit Titel und Text — und nur, wenn sich gegenueber der
 * geltenden Fassung etwas aendert. Eine inhaltsgleiche Fassung n+1 waere
 * Laerm in der Historie und liesse Teilnahmen scheinbar eine andere Fassung
 * bestaetigen.
 */
export const kannSpeichern = (e: {
  titel: string;
  text: string;
  dirty: boolean;
  geltend: Pick<DatenschutzText, 'titel' | 'text'> | null;
}): boolean => {
  if (!e.dirty || !(e.titel ?? '').trim() || !(e.text ?? '').trim()) return false;
  if (!e.geltend) return true;
  return vergleichsText(e.titel) !== vergleichsText(e.geltend.titel)
    || vergleichsText(e.text) !== vergleichsText(e.geltend.text);
};

/** „Fassung 3 · 07.10.2026, 14:30 · von Max Muster" fuer die Versionsliste. */
export const fassungZeile = (f: Pick<DatenschutzFassung, 'fassung' | 'createdAt' | 'erstelltVonName'>): string => {
  const teile = [`Fassung ${f.fassung}`];
  if (f.createdAt) teile.push(formatZeit(f.createdAt));
  const wer = (f.erstelltVonName || '').trim();
  if (wer) teile.push(`von ${wer}`);
  return teile.join(' · ');
};
